import { ModuleData } from '@/components/filters/types';
import { ComponentId } from '@core/store/slices/apiSlice';

/**
 * Format date time - luxon formatting
 * @param date - Date to format
 * @param locale - Locale for formatting
 * @returns Formatted date string
 */
export const formatDateTime = (date: Date | string, locale: string = 'en'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    return dateObj.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

/**
 * Format time only
 * @param date - Date to format
 * @param locale - Locale for formatting
 * @returns Formatted time string
 */
export const formatTime = (date: Date | string, locale: string = 'en'): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    return dateObj.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });
};

/**
 * Check if device is mobile
 * @returns Boolean indicating if device is mobile
 */
export const isMobileDevice = (): boolean => {
    const WIDTH = 992;
    const RATIO = 3;
    const rect = document.body.getBoundingClientRect();
    return rect.width - RATIO < WIDTH;
};

/**
 * Set viewport height CSS variable
 */
export const setViewportHeight = (): void => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
};

/**
 * Scroll to top of content area
 */
export const scrollToTop = (): void => {
    setTimeout(() => {
        const contentWrapper = document.querySelector('.v-content__wrap');
        if (contentWrapper) {
            contentWrapper.scrollTop = 0;
        }

        const boxedLayout = document.querySelector('.app-boxed-layout .app-content');
        if (boxedLayout) {
            boxedLayout.scrollTop = 0;
        }

        const miniLayout = document.querySelector('.app-mini-layout .app-content');
        if (miniLayout) {
            miniLayout.scrollTop = 0;
        }
    }, 200);
};

/**
 * Base64 encode string
 * @param str - String to encode
 * @returns Base64 encoded string
 */
export const base64Encode = (str: string): string => {
    return btoa(str);
};

/**
 * Base64 decode string
 * @param str - Base64 string to decode
 * @returns Decoded string
 */
export const base64Decode = (str: string): string => {
    try {
        return atob(str);
    } catch (error) {
        console.error('Failed to decode base64 string:', error);
        return '';
    }
};

/**
 * Deep clone object (using structured cloning)
 * @param obj - Object to clone
 * @returns Cloned object
 */
export const deepClone = <T>(obj: T): T => {
    try {
        return structuredClone(obj);
    } catch (error) {
        // Fallback for older browsers
        return JSON.parse(JSON.stringify(obj));
    }
};

/**
 * Debounce function execution
 * @param func - Function to debounce
 * @param wait - Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;

    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

/**
 * Throttle function execution
 * @param func - Function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 */
export const throttle = <T extends (...args: any[]) => any>(
    func: T,
    limit: number
): ((...args: Parameters<T>) => void) => {
    let inThrottle: boolean;

    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => (inThrottle = false), limit);
        }
    };
};

/**
 * Generate unique ID
 * @returns Unique ID string
 */
export const generateId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param value - Value to check
 * @returns Boolean indicating if value is empty
 */
export const isEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};

/**
 * Format file size to human readable format
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number, decimals: number = 2): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

/**
 * Capitalize first letter of string
 * @param str - String to capitalize
 * @returns Capitalized string
 */
export const capitalize = (str: string): string => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Convert camelCase to kebab-case
 * @param str - CamelCase string
 * @returns kebab-case string
 */
export const camelToKebab = (str: string): string => {
    return str.replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2').toLowerCase();
};

/**
 * Convert kebab-case to camelCase
 * @param str - kebab-case string
 * @returns camelCase string
 */
export const kebabToCamel = (str: string): string => {
    return str.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
};

/**
 * Format number with thousand separators
 * @param num - Number to format
 * @param locale - Locale for formatting
 * @returns Formatted number string
 */
export const formatNumber = (num: number, locale: string = 'en-US'): string => {
    return new Intl.NumberFormat(locale).format(num);
};

/**
 * Format currency
 * @param amount - Amount to format
 * @param currency - Currency code
 * @param locale - Locale for formatting
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = 'EUR', locale: string = 'de-DE'): string => {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
    }).format(amount);
};

/**
 * Truncate text to specified length with optional ending
 * @param str
 * @param length
 * @param ending
 */
export function textTruncate(str: string, length?: number, ending?: string): string {
    const textLength = length ?? 10;
    const textEnding = ending ?? '...';

    if (str.length > textLength) {
        return str.substring(0, textLength - textEnding.length) + textEnding;
    }
    return str;
}

/**
 * Function to convert hex to rgba
 */
export function hexToRgbA(hex: string, alpha: number = 1): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return hex;

    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);

    return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Get current app layout
 * @param pathname - Current pathname
 * @returns Layout type
 */
export function getCurrentAppLayout(pathname: string): string {
    const pathParts = pathname.split('/');
    return pathParts[1] || '';
}

/**
 * Function to calculate percentage based on actual/max
 */
export function percent(max: number, value?: number): number {
    if (!value) return 0;
    return Math.round((value / max) * 100);
}

/**
 * Compare if two strings are equal (case insensitive)
 */
export function equalsIgnoreCase(text?: string, other?: string): boolean {
    if (!text) return !other;
    if (!other) return false;
    return text.localeCompare(other, undefined, { sensitivity: 'base' }) === 0;
}

/**
 * Converts ComponentId array to ModuleData array for a specific module type.
 * Filters out disabled and deleted components, and matches by discriminator or type.
 *
 * @param componentIds - Array of component IDs from the API store
 * @param moduleType - The module type to filter by (case insensitive)
 * @returns Array of ModuleData with deviceId and proper type conversions
 */

export const convertComponentsToModuleData = (
    componentIds: ComponentId[],
    moduleType: string,
    shopId?: string | null
): ModuleData[] => {
    const normalizedType = moduleType.toLowerCase();

    const filtered = componentIds.filter((c) => {
        const matchesShop = !shopId || c.shopId === shopId;
        const matchesType =
            c.discriminator.toLowerCase() === normalizedType ||
            (c.type && c.type.toLowerCase().includes(normalizedType));
        const isActive = !c.disabled && !c.deleteDate;

        return isActive && matchesShop && matchesType;
    });

    return filtered.map((c) => ({
        id: c.id,
        shopId: c.shopId,
        deviceId: c.deviceId,
        deleteDate: c.deleteDate ?? undefined,
        disabled: c.disabled
    }));
};
